import type { App } from 'vue';
import type { Router } from 'vue-router';

import vueMsalPlugin, {
    useMsalStore,
    type PublicClientApplication,
} from '@precon-tools-suite-by-shared/vue-msal';

import vueApplicationInsightsPlugin from '@precon-tools-suite-by-shared/vue-application-insights';

import {
    provideApolloClient,
    DefaultApolloClient,
} from '@vue/apollo-composable';
import { getScopes } from '@precon-tools-suite-by-shared/shared-app-configuration-js-browser';
import { createApplicationInsightsInstance } from './lib/application-insights';
import { createBackendApolloClient } from './lib/apollo-client';
import {
    type MsalOptions,
    createBackendMsalInstance,
    getB2CPolicyAuthority,
} from './lib/msal';
import type { DocumentNode } from '@apollo/client/core';
import type { FragmentRegistryAPI } from '@apollo/client/cache';
import type { Pinia } from 'pinia';
import { getMetaOrFail } from '@precon-tools-suite-by-shared/browser-utils';

export type BackendClientOptions = MsalOptions & {
    router?: Router;
    pinia?: Pinia | undefined | null;
    loginPolicy?: string;
    profileEditPolicy?: string;
    passwordResetPolicy?: string;
    appName: string;
    appVersion: string;
    loginExtraQueryParameters?: Record<string, string | null | undefined>;
    scopes: Record<string, string[]> & { PreconBackend: string[] };
    graphqlFragments?: FragmentRegistryAPI | DocumentNode[];
    connectToDevTools?: boolean;
    logGraphQLErrors?: boolean;
    logGraphQLRoundTrip?: boolean;
    instrumentationKey?: string;
    b2cTenantDomain?: string;
    maxRetryAttempts?: number;
    maxRetryDelay?: number;
    initialRetryDelay?: number;
};

export default {
    install(app: App, options: BackendClientOptions) {
        // Initialize application insights.
        const appInsights = createApplicationInsightsInstance(options);
        if (appInsights) {
            appInsights.loadAppInsights();
            app.use(vueApplicationInsightsPlugin, {
                appInsights,
                router: options.router,
            });
        }

        // Initialize msal.
        const msalInstance: PublicClientApplication =
            createBackendMsalInstance(options);

        app.use(vueMsalPlugin, {
            router: options.router,
            msalInstance,
        });

        const loginScopes = getScopes(options.scopes);

        const msalStore = useMsalStore(options.pinia);
        msalStore.addLoginScopes(...loginScopes);
        msalStore.setPasswordResetAuthority(
            getB2CPolicyAuthority(
                options.passwordResetPolicy ??
                    getMetaOrFail('b2c-password-reset-policy-name'),
                options.b2cTenantDomain
            )
        );
        msalStore.setProfileEditAuthority(
            getB2CPolicyAuthority(
                options.profileEditPolicy ??
                    getMetaOrFail('b2c-profile-edit-policy-name'),
                options.b2cTenantDomain
            )
        );
        if (options.loginExtraQueryParameters) {
            msalStore.addLoginQueryParameters(
                Object.fromEntries(
                    Object.entries(options.loginExtraQueryParameters).filter(
                        (s): s is [string, string] => typeof s[1] === 'string'
                    )
                )
            );
        }

        // Initialize apollo.
        const apolloClient = createBackendApolloClient({
            appInsights,
            appName: options.appName,
            appVersion: options.appVersion,
            graphqlFragments: options.graphqlFragments,
            connectToDevTools: options.connectToDevTools,
            logGraphQLErrors: options.logGraphQLErrors,
            logGraphQLRoundTrip: options.logGraphQLRoundTrip,
            async aquireBearerToken() {
                const tokenResponse = await msalStore.acquireToken({
                    scopes: [
                        'openid',
                        ...getScopes({
                            PreconBackend: options.scopes.PreconBackend,
                        }),
                    ],
                });
                return tokenResponse.accessToken;
            },
            retryOptions: {
                initialDelay: options.initialRetryDelay,
                maxDelay: options.maxRetryDelay,
                jitter: true,
                maxAttempts: options.maxRetryAttempts,
            },
        });

        app.provide(DefaultApolloClient, apolloClient);
        provideApolloClient(apolloClient);
    },
};
